import { type ReactNode, useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  Ban,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  CircleDot,
  ListTodo,
  Loader2,
  type LucideIcon,
  Pause,
  Pencil,
  Play,
  Target,
  X,
  XCircle,
} from "lucide-react";

import {
  cancelMission,
  getMission,
  listMissions,
  pauseMission,
  resumeMission,
  setMissionBudget,
  type Mission,
  type MissionDetailView,
  type MissionEvent,
  type MissionTask,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { cn } from "@/lib/utils";

const ucToCredits = (uc: number) => Math.round(uc / 1_000_000);

function missionTitle(m: Mission): string {
  return m.title?.trim() || m.objective.slice(0, 60);
}

// useSessionMissions loads + polls the missions started FROM THIS chat session
// (scoped to conversationId), bucketed by lifecycle. One poller shared by the
// sticky dock (ongoing) and the inline results (finished).
export function useSessionMissions(projectId: string, conversationId: string) {
  const [missions, setMissions] = useState<Mission[]>([]);

  const reload = useCallback(() => {
    listMissions(projectId)
      .then(setMissions)
      .catch(() => {
        /* transient; keep last */
      });
  }, [projectId]);

  useEffect(() => {
    reload();
    const t = window.setInterval(reload, 6000);
    return () => window.clearInterval(t);
  }, [reload]);

  // Only ONGOING missions live in the dock; finished ones render as a persistent
  // breakdown card inline with the agent's result message (MissionResultCard).
  const mine = conversationId ? missions.filter((m) => m.conversation_id === conversationId) : [];
  return {
    awaiting: mine.filter((m) => m.status === "awaiting_plan_approval"),
    blocked: mine.filter((m) => m.status === "blocked"),
    running: mine.filter((m) => m.status === "active" || m.status === "ticking"),
    paused: mine.filter((m) => m.status === "paused"),
    reload,
  };
}

export type SessionMissions = ReturnType<typeof useSessionMissions>;

// OngoingMissions renders the live mission cards you steer (running / paused /
// blocked / revising) INLINE in the chat transcript — the whole session is focused on
// the mission, so its progress reads as part of the conversation rather than a tall
// floating panel above the input with its own scrollbar. Awaiting-approval is handled
// separately (InlinePlanReview + the composer plan bar). Renders nothing when idle.
export function OngoingMissions({ projectId, m, revisingId }: { projectId: string; m: SessionMissions; revisingId?: string | null }) {
  if (m.blocked.length + m.running.length + m.paused.length === 0) return null;
  return (
    <div className="space-y-2">
      {m.blocked.map((mi) => (
        <div key={mi.id} className="rounded-xl border border-warning/40 bg-warning/5 p-3 text-sm">
          <div className="flex items-center gap-2 font-medium">
            <Pause className="h-4 w-4 text-warning" /> {missionTitle(mi)} is waiting on your approval to continue
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Approve it in the agent's pending queue (Settings → Governance) — it then resumes on its own.
          </p>
        </div>
      ))}
      {m.running.map((mi) =>
        // A mission that's re-planning after a revise transiently looks "running"
        // (ticking). Show a passive "Revising…" card instead of the steerable
        // RunningCard, so it never reads as "it started without my approval".
        mi.id === revisingId ? (
          <div key={mi.id} className="rounded-xl border border-primary/25 bg-primary/[0.03] p-3 text-sm">
            <div className="flex items-center gap-2 font-medium text-foreground">
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" /> Revising your plan…
            </div>
            <p className="mt-1 text-xs text-muted-foreground">Applying your changes — I'll show the updated plan to approve in a moment.</p>
          </div>
        ) : (
          <RunningCard key={mi.id} projectId={projectId} mission={mi} onChanged={m.reload} />
        ),
      )}
      {m.paused.map((mi) => (
        <PausedCard key={mi.id} projectId={projectId} mission={mi} onChanged={m.reload} />
      ))}
    </div>
  );
}

// humanizePauseReason explains, in plain language, why a mission paused — for a
// non-technical owner reading the inline card. Mirrors the engine's chat message.
function humanizePauseReason(r?: string): string {
  switch (r) {
    case "agent_unreachable":
      return "I couldn't reach the agent for a few minutes (it may have been redeploying or asleep). Nothing's lost — resume when you're ready.";
    case "no_progress":
      return "I went several steps without making real progress, so I stopped rather than spin. Resume to keep trying, or steer me first.";
    case "budget_exhausted":
      return "It reached its credit budget.";
    case "tick_cap":
      return "It hit its step limit.";
    case "tripped_kill":
      return "The workspace was frozen.";
    case "agent_budget":
      return "It reached the agent's spending limit.";
    case "manual":
      return "You paused it.";
    default:
      // Free-text reason (e.g. an agent's mission_blocked message) — render verbatim;
      // the underscore→space normalization is only for the enum reasons above.
      return r || "It was paused.";
  }
}

// ── shared presentational bits ───────────────────────────────────────────────

// TaskIcon: the spinner ONLY animates when the mission is actually running — a
// stopped/paused mission shows an in-progress step as a static dot, never spinning.
function TaskIcon({ status, live }: { status: string; live: boolean }) {
  if (status === "done") return <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-success" />;
  if (status === "failed") return <XCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />;
  if (status === "blocked") return <Pause className="h-3.5 w-3.5 shrink-0 text-warning" />;
  if (status === "doing")
    return live ? (
      <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" />
    ) : (
      <CircleDot className="h-3.5 w-3.5 shrink-0 text-primary/70" />
    );
  return <Circle className="h-3.5 w-3.5 shrink-0 text-muted-foreground/40" />;
}

// ── plan rendering ───────────────────────────────────────────────────────────
// A mission's task list can be 3 or 30+ steps. Rather than a flat scrolling wall,
// PlanChecklist groups by ATTENTION (needs-you → working-now → up-next → done) so
// the visible height stays ~constant regardless of task count. Small plans (≤6)
// keep the original flat list (zero regression). The "approval" surface shows the
// full numbered plan in a height-capped, internally-scrolling region so the
// Approve/Reject buttons never get pushed off-screen.

const SMALL_MAX = 6; // ≤6 tasks → flat list, no grouping machinery
const PEEK = 3; // pending rows shown before "Show N more"

type Surface = "approval" | "live" | "result";
type Bucket = "pending" | "doing" | "done" | "failed" | "blocked";

function bucketTasks(tasks: MissionTask[]) {
  const by = (s: Bucket) => tasks.filter((t) => t.status === s).sort((a, b) => a.seq - b.seq);
  const done = by("done");
  return {
    attention: [...by("failed"), ...by("blocked")], // failed first (most urgent)
    now: by("doing"),
    pending: by("pending"),
    done,
    counts: {
      done: done.length,
      doing: by("doing").length,
      pending: by("pending").length,
      blocked: by("blocked").length,
      failed: by("failed").length,
      total: tasks.length,
      pct: tasks.length ? Math.round((done.length / tasks.length) * 100) : 0,
    },
  };
}

type Counts = ReturnType<typeof bucketTasks>["counts"];

// ProgressHeader: the bar (red on failure) + an amber "needs you" chip + an
// "N of M" count — the one line that answers "how far / anything wrong" at a glance.
function ProgressHeader({ counts }: { counts: Counts }) {
  const attentionN = counts.failed + counts.blocked;
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
        <div
          className={cn("h-full rounded-full transition-all duration-500", counts.failed ? "bg-destructive/80" : "bg-success")}
          style={{ width: `${counts.pct}%` }}
        />
      </div>
      {attentionN > 0 && (
        <span className="flex shrink-0 items-center gap-1 rounded-full bg-warning/15 px-1.5 py-0.5 text-[10px] font-medium text-warning">
          <AlertTriangle className="h-3 w-3" />
          {attentionN}
        </span>
      )}
      <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
        {counts.done} of {counts.total}
      </span>
    </div>
  );
}

// StatusLedger: the plain-language count line ("7 done · 1 now · 1 waiting · 3 to go"),
// zero buckets hidden, dot-coded. The "now" dot pulses while live.
function StatusLedger({ counts, live }: { counts: Counts; live: boolean }) {
  const pills = [
    counts.done > 0 && { dot: "bg-success", n: counts.done, label: "done" },
    counts.doing > 0 && { dot: "bg-primary", n: counts.doing, label: "now", spin: live },
    counts.blocked > 0 && { dot: "bg-warning", n: counts.blocked, label: "waiting" },
    counts.failed > 0 && { dot: "bg-destructive", n: counts.failed, label: "failed" },
    counts.pending > 0 && { dot: "bg-muted-foreground/40", n: counts.pending, label: "to go" },
  ].filter(Boolean) as { dot: string; n: number; label: string; spin?: boolean }[];
  if (pills.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
      {pills.map((p) => (
        <span key={p.label} className="inline-flex items-center gap-1.5">
          <span className={cn("h-1.5 w-1.5 rounded-full", p.dot, p.spin && "animate-pulse")} />
          <span className="tabular-nums">{p.n}</span> {p.label}
        </span>
      ))}
    </div>
  );
}

// Band: an always-open, tinted container for the steps that matter right now
// (needs-you / working-now).
function Band({
  tone,
  icon: Icon,
  label,
  spin = false,
  children,
}: {
  tone: "attention" | "now";
  icon: LucideIcon;
  label: string;
  spin?: boolean;
  children: ReactNode;
}) {
  const frame = tone === "attention" ? "border-warning/30 bg-warning/[0.05]" : "border-border bg-muted/40";
  const text = tone === "attention" ? "text-warning" : "text-muted-foreground";
  return (
    <div className={cn("rounded-lg border px-2.5 py-2", frame)}>
      <div className={cn("mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide", text)}>
        <Icon className={cn("h-3 w-3", spin && "animate-spin")} />
        {label}
      </div>
      <ul className="space-y-1">{children}</ul>
    </div>
  );
}

// PeekGroup: the pending "Up next" list — shows PEEK rows, folds the rest behind
// a "Show N more" toggle so a long backlog never floods the card.
function PeekGroup({ tasks, live }: { tasks: MissionTask[]; live: boolean }) {
  const [showAll, setShowAll] = useState(false);
  if (tasks.length === 0) return null;
  const shown = showAll ? tasks : tasks.slice(0, PEEK);
  const rest = tasks.length - PEEK;
  return (
    <div className="px-0.5">
      <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        <ListTodo className="h-3 w-3" /> Up next · {tasks.length}
      </div>
      <ul className="space-y-1">
        {shown.map((t) => (
          <TaskRow key={t.id} task={t} live={live} variant="muted" />
        ))}
      </ul>
      {rest > 0 && (
        <button onClick={() => setShowAll((s) => !s)} className="ml-[22px] mt-1 text-[11px] font-medium text-muted-foreground hover:text-foreground">
          {showAll ? "Show less" : `Show ${rest} more`}
        </button>
      )}
    </div>
  );
}

// DoneGroup: completed work folded to a single "Done · N" drawer (open by default
// only on the finished-result surface and on small plans).
function DoneGroup({ tasks, live, defaultOpen }: { tasks: MissionTask[]; live: boolean; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  if (tasks.length === 0) return null;
  return (
    <div className="px-0.5">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground">
        <ChevronRight className={cn("h-3 w-3 transition-transform", open && "rotate-90")} />
        <CheckCircle2 className="h-3 w-3 text-success" />
        Done · {tasks.length}
      </button>
      {open && (
        <ul className="mt-1 space-y-1">
          {tasks.map((t) => (
            <TaskRow key={t.id} task={t} live={live} variant="done" />
          ))}
        </ul>
      )}
    </div>
  );
}

// ApprovalList: the pre-run plan the owner approves — the FULL numbered plan. It now
// renders inside a chat-transcript card (InlinePlanReview), so it grows to its natural
// height and scrolls WITH THE PAGE — no internal height cap (that was for the old
// floating dock card and would create a scroll-trap-within-scroll here). Numbered
// (sequence carries meaning); no per-row status icons (all pending pre-run).
function ApprovalList({ tasks }: { tasks: MissionTask[] }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        <ListTodo className="h-3 w-3" /> The plan · {tasks.length} steps
      </div>
      <div className="rounded-lg border bg-card">
        <ol className="space-y-1 px-2.5 py-2">
          {tasks.map((t, i) => (
            <li key={t.id} className="flex items-start gap-2 text-[13px] leading-snug">
              <span className="mt-0.5 w-5 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground/60">{i + 1}</span>
              <ApprovalRow task={t} />
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

// ApprovalRow: a TaskRow body without the status icon (the numbered list carries order).
function ApprovalRow({ task }: { task: MissionTask }) {
  const [open, setOpen] = useState(false);
  const title = task.title?.trim() || task.description;
  const detail = task.description?.trim();
  const hasDetail = !!task.title?.trim() && !!detail && detail !== task.title.trim();
  return (
    <div className="min-w-0 flex-1">
      {hasDetail ? (
        <button className="flex w-full items-start gap-1 text-left" onClick={() => setOpen((o) => !o)}>
          <span className="line-clamp-1 text-foreground">{title}</span>
          <ChevronDown className={cn("mt-0.5 h-3 w-3 shrink-0 text-muted-foreground/60 transition-transform", open && "rotate-180")} />
        </button>
      ) : (
        <span className="line-clamp-1 text-foreground">{title}</span>
      )}
      {open && hasDetail && <p className="mt-0.5 text-xs text-muted-foreground">{detail}</p>}
    </div>
  );
}

// PlanChecklist orchestrates the bands. surface picks the layout: "approval" =
// full numbered plan (height-capped), "live" = grouped attention-first, "result" =
// grouped with Done expanded. Small plans render the original flat list.
function PlanChecklist({ tasks, live, surface = "live" }: { tasks: MissionTask[]; live: boolean; surface?: Surface }) {
  if (tasks.length === 0) {
    return <div className="rounded-md border border-dashed px-3 py-2.5 text-center text-xs text-muted-foreground">Planning…</div>;
  }
  const b = bucketTasks(tasks);
  const small = tasks.length <= SMALL_MAX;

  // Small plans (outside approval): the original flat list — zero regression.
  if (small && surface !== "approval") {
    return (
      <div className="space-y-1.5">
        <ProgressHeader counts={b.counts} />
        <ul className="space-y-0.5">
          {tasks.map((t) => (
            <TaskRow key={t.id} task={t} live={live} />
          ))}
        </ul>
      </div>
    );
  }

  // Open the Done drawer by default on the result card, and also whenever Done is
  // the ONLY content (e.g. the final step flips to done a poll before the mission is
  // marked finished) — otherwise the live card would briefly look empty at the finish line.
  const doneDefaultOpen = surface === "result" || (b.attention.length === 0 && b.now.length === 0 && b.pending.length === 0);
  return (
    <div className="space-y-2">
      <ProgressHeader counts={b.counts} />
      <StatusLedger counts={b.counts} live={live} />

      {b.attention.length > 0 && (
        <Band tone="attention" icon={AlertTriangle} label="Needs you">
          {b.attention.map((t) => (
            <TaskRow key={t.id} task={t} live={live} variant="attention" />
          ))}
        </Band>
      )}

      {b.now.length > 0 && (
        <Band tone="now" icon={live ? Loader2 : CircleDot} label="Working on now" spin={live}>
          {b.now.map((t) => (
            <TaskRow key={t.id} task={t} live={live} variant="now" />
          ))}
        </Band>
      )}

      {surface === "approval" ? <ApprovalList tasks={b.pending} /> : <PeekGroup tasks={b.pending} live={live} />}

      <DoneGroup tasks={b.done} live={live} defaultOpen={doneDefaultOpen} />
    </div>
  );
}

// TaskRow shows the step's short TITLE in the list; if it has extra detail, the row
// is expandable (click to reveal the one-line description) — keeps the list scannable.
// variant overrides the title tone for the grouped bands; "auto" keeps the original
// status-derived styling (used by the small flat list).
type RowVariant = "attention" | "now" | "muted" | "done" | "auto";
const ROW_TONE: Record<Exclude<RowVariant, "auto">, string> = {
  attention: "text-foreground",
  now: "font-medium text-foreground",
  muted: "text-muted-foreground",
  done: "text-muted-foreground line-through",
};

function TaskRow({ task, live, variant = "auto" }: { task: MissionTask; live: boolean; variant?: RowVariant }) {
  const [open, setOpen] = useState(false);
  const title = task.title?.trim() || task.description;
  const detail = task.description?.trim();
  const hasDetail = !!task.title?.trim() && !!detail && detail !== task.title.trim();
  const autoTone = cn(
    task.status === "done" && "text-muted-foreground line-through",
    task.status === "failed" && "text-muted-foreground",
    task.status === "doing" && "font-medium text-foreground",
    (task.status === "pending" || task.status === "blocked") && "text-muted-foreground",
  );
  const titleCls = cn("line-clamp-1", variant === "auto" ? autoTone : ROW_TONE[variant]);
  return (
    <li className="text-[13px] leading-snug">
      <div className="flex items-start gap-2">
        <span className="mt-0.5">
          <TaskIcon status={task.status} live={live} />
        </span>
        {hasDetail ? (
          <button className="flex min-w-0 flex-1 items-start gap-1 text-left" onClick={() => setOpen((o) => !o)}>
            <span className={titleCls}>{title}</span>
            <ChevronDown className={cn("mt-0.5 h-3 w-3 shrink-0 text-muted-foreground/60 transition-transform", open && "rotate-180")} />
          </button>
        ) : (
          <span className={titleCls}>{title}</span>
        )}
      </div>
      {open && hasDetail && <p className="ml-[22px] mt-0.5 text-xs text-muted-foreground">{detail}</p>}
    </li>
  );
}

// humanizeEvent turns a raw timeline event into a plain-language line for a
// non-technical owner — or "" to hide it (noise like per-tick/per-task records,
// which the plan ledger already shows; and anything that's just an id).
function humanizeEvent(ev: MissionEvent): string {
  switch (ev.kind) {
    case "planned":
      return "Drew up a plan";
    case "awaiting_plan_approval":
      return "Waiting for your approval";
    case "plan_approved":
      return "Plan approved — getting to work";
    case "plan_rejected":
      return "Plan sent back for changes";
    case "waiting":
      return "Pausing until later";
    case "paused":
      return "Paused";
    case "blocked":
      return "Waiting on an approval";
    case "done":
      return "Finished";
    case "failed":
      return "Stopped";
    case "note":
      return ev.summary || ""; // the agent's own progress note
    default:
      return ""; // tick_started / task_done / task_failed → covered by the plan above
  }
}

// activityRows turns events into newest-first plain-language lines (ids are random,
// so sort by ts), dropping the noise humanizeEvent hides.
function activityRows(events: MissionEvent[]) {
  return events
    .map((ev) => ({ ev, label: humanizeEvent(ev) }))
    .filter((r) => r.label !== "")
    .sort((a, b) => new Date(b.ev.ts).getTime() - new Date(a.ev.ts).getTime());
}

function Timeline({ events }: { events: MissionEvent[] }) {
  const fmt = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  };
  const rows = activityRows(events);
  if (rows.length === 0) return <p className="text-xs text-muted-foreground">No activity yet.</p>;
  return (
    <ul className="space-y-1 text-xs">
      {rows.slice(0, 6).map(({ ev, label }) => (
        <li key={ev.id} className="flex items-baseline gap-2">
          <span className="shrink-0 tabular-nums text-muted-foreground">{fmt(ev.ts)}</span>
          <span className="line-clamp-2 text-muted-foreground">{label}</span>
        </li>
      ))}
    </ul>
  );
}

// MissionDetail fetches + (optionally) live-polls the plan; the verbose activity
// log is collapsed by default so the detail stays compact and scannable.
function MissionDetail({ projectId, missionId, live }: { projectId: string; missionId: string; live: boolean }) {
  const [detail, setDetail] = useState<MissionDetailView | null>(null);
  const [showActivity, setShowActivity] = useState(false);
  useEffect(() => {
    let active = true;
    const fetchOnce = () =>
      getMission(projectId, missionId)
        .then((d) => active && setDetail(d))
        .catch(() => {});
    fetchOnce();
    if (!live) return () => {
      active = false;
    };
    const t = window.setInterval(fetchOnce, 5000);
    return () => {
      active = false;
      window.clearInterval(t);
    };
  }, [projectId, missionId, live]);

  if (!detail) {
    return (
      <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
      </div>
    );
  }
  const running = detail.mission.status === "active" || detail.mission.status === "ticking";
  const activityCount = activityRows(detail.events).length;
  return (
    <div className="mt-2 space-y-2 border-t pt-2">
      <PlanChecklist tasks={detail.tasks} live={running} surface="live" />
      {activityCount > 0 && (
        <div>
          <button
            className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground"
            onClick={() => setShowActivity((s) => !s)}
          >
            <ChevronDown className={cn("h-3 w-3 transition-transform", showActivity && "rotate-180")} /> Activity ({activityCount})
          </button>
          {showActivity && <div className="mt-1.5">{<Timeline events={detail.events} />}</div>}
        </div>
      )}
    </div>
  );
}

// ── plan review (chat-native) ────────────────────────────────────────────────
// InlinePlanReview renders a pending mission's plan as a card in the transcript —
// the agent "presenting" what it intends to do. The Approve / Discard / revise
// actions live in the composer's plan bar (PlanBar in AgentChatPanel), pinned by
// the input so they're always reachable. So this card is purely the plan itself:
// premium, as tall as it needs, scrolling with the page — not a floating dock card.
export function InlinePlanReview({ projectId, mission }: { projectId: string; mission: Mission }) {
  const [tasks, setTasks] = useState<MissionTask[] | null>(null);
  useEffect(() => {
    let active = true;
    getMission(projectId, mission.id)
      .then((d) => active && setTasks(d.tasks))
      .catch(() => active && setTasks([]));
    return () => {
      active = false;
    };
    // updated_at changes when the plan is revised → refetch the new steps.
  }, [projectId, mission.id, mission.updated_at]);

  return (
    <div id={`mission-plan-${mission.id}`} className="rounded-2xl border border-primary/25 bg-primary/[0.03] p-4 text-sm">
      <div className="flex items-center gap-2 font-medium text-foreground">
        <Target className="h-4 w-4 text-primary" /> Here's my plan — {missionTitle(mission)}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Review the steps below. Approve it by the message box to start — or just tell me what to change and I'll revise it.
      </p>
      <div className="mt-3">{tasks === null ? <MiniLoading /> : <PlanChecklist tasks={tasks} live={false} surface="approval" />}</div>
    </div>
  );
}

function MiniLoading() {
  return (
    <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading the plan…
    </div>
  );
}

// BudgetControl shows spend (and the per-mission cap if any) and lets the owner
// raise/lower it inline — or clear it (empty = no limit). A mission has NO
// per-mission budget by default; this is how you add or change one, including
// lifting a budget_exhausted pause (set higher, then Resume).
function BudgetControl({ projectId, mission, onChanged }: { projectId: string; mission: Mission; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const save = async () => {
    const n = Math.max(0, Math.round(Number(val) || 0));
    setBusy(true);
    try {
      await setMissionBudget(projectId, mission.id, n);
      toast.success(n === 0 ? "Budget limit removed." : `Budget set to ${n} credits.`);
      setEditing(false);
      onChanged();
    } catch {
      toast.error("Couldn't update the budget.");
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <input
          type="number"
          min={0}
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void save();
            if (e.key === "Escape") setEditing(false);
          }}
          placeholder="no limit"
          className="h-6 w-20 rounded border bg-background px-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
        />
        <span>credits</span>
        <button className="text-success hover:opacity-80 disabled:opacity-50" title="Save" disabled={busy} onClick={() => void save()}>
          <Check className="h-3.5 w-3.5" />
        </button>
        <button className="text-muted-foreground hover:text-foreground" title="Cancel" onClick={() => setEditing(false)}>
          <X className="h-3.5 w-3.5" />
        </button>
      </span>
    );
  }
  return (
    <button
      className="inline-flex items-center gap-1 hover:text-foreground"
      title="Edit budget"
      onClick={() => {
        setVal(mission.budget_uc > 0 ? String(ucToCredits(mission.budget_uc)) : "");
        setEditing(true);
      }}
    >
      <span>
        {ucToCredits(mission.spent_uc)}
        {mission.budget_uc > 0 ? `/${ucToCredits(mission.budget_uc)}` : ""} credits
      </span>
      <Pencil className="h-3 w-3 opacity-40" />
    </button>
  );
}

// ── running ──────────────────────────────────────────────────────────────────

function RunningCard({ projectId, mission, onChanged }: { projectId: string; mission: Mission; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const toast = useToast();

  return (
    <div className="rounded-xl border bg-card p-3 text-sm">
      <div className="flex items-center justify-between gap-2">
        <button className="flex min-w-0 items-center gap-2 text-left" onClick={() => setOpen((o) => !o)}>
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" />
          <span className="truncate font-medium">{missionTitle(mission)}</span>
          <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")} />
        </button>
        <div className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
          {(mission.tasks_total ?? 0) > 0 && (
            <span title="Steps completed">
              {mission.tasks_done ?? 0}/{mission.tasks_total} steps
            </span>
          )}
          <BudgetControl projectId={projectId} mission={mission} onChanged={onChanged} />
          <button
            className="text-muted-foreground hover:text-foreground"
            title="Pause"
            onClick={async () => {
              try {
                await pauseMission(projectId, mission.id);
                toast.success("Paused.");
                onChanged();
              } catch {
                toast.error("Couldn't pause.");
              }
            }}
          >
            <Pause className="h-3.5 w-3.5" />
          </button>
          <button
            className="text-muted-foreground hover:text-destructive"
            title="Cancel"
            onClick={async () => {
              if (!window.confirm("Cancel this mission? This can't be undone.")) return;
              try {
                await cancelMission(projectId, mission.id);
                toast.success("Cancelled.");
                onChanged();
              } catch {
                toast.error("Couldn't cancel.");
              }
            }}
          >
            <Ban className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {open && <MissionDetail projectId={projectId} missionId={mission.id} live />}
    </div>
  );
}

// ── paused — visible + resumable inline ──────────────────────────────────────
// A paused mission never disappears: it shows why it paused + a Resume button. To
// steer/edit it, the user just talks in the chat (the agent relays it) — there's no
// separate steer box; the chat is the single input, like Telegram/Slack.
function PausedCard({ projectId, mission, onChanged }: { projectId: string; mission: Mission; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const toast = useToast();

  return (
    <div className="rounded-xl border border-warning/40 bg-warning/[0.05] p-3 text-sm">
      <div className="flex items-center justify-between gap-2">
        <button className="flex min-w-0 items-center gap-2 text-left" onClick={() => setOpen((o) => !o)}>
          <Pause className="h-3.5 w-3.5 shrink-0 text-warning" />
          <span className="truncate font-medium">{missionTitle(mission)}</span>
          <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")} />
        </button>
        <div className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
          {(mission.tasks_total ?? 0) > 0 && (
            <span title="Steps completed">
              {mission.tasks_done ?? 0}/{mission.tasks_total} steps
            </span>
          )}
          <BudgetControl projectId={projectId} mission={mission} onChanged={onChanged} />
          <button
            className="text-muted-foreground hover:text-destructive"
            title="Cancel"
            onClick={async () => {
              if (!window.confirm("Cancel this mission? This can't be undone.")) return;
              try {
                await cancelMission(projectId, mission.id);
                toast.success("Cancelled.");
                onChanged();
              } catch {
                toast.error("Couldn't cancel.");
              }
            }}
          >
            <Ban className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <p className="mt-1.5 text-xs text-muted-foreground">Paused — {humanizePauseReason(mission.pause_reason)}</p>

      {open && <MissionDetail projectId={projectId} missionId={mission.id} live={false} />}

      <div className="mt-2.5 flex items-center gap-2">
        <Button
          size="sm"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await resumeMission(projectId, mission.id);
              toast.success("Resumed — I'm back on it.");
              onChanged();
            } catch {
              toast.error("Couldn't resume.");
            } finally {
              setBusy(false);
            }
          }}
        >
          <Play className="h-4 w-4" /> Resume
        </Button>
        <span className="text-[11px] text-muted-foreground">Want changes first? Just tell me here in the chat, then resume.</span>
      </div>
    </div>
  );
}

// ── finished (done / failed) — a collapsed breakdown card ────────────────────
// MissionResultCard is the PERSISTENT breakdown of a finished mission, rendered in
// the transcript ABOVE the agent's final summary message (tagged by mission_id). It
// stays in history — NOT dismissable — and fetches the mission by id (so it works on
// reload). Collapsed by default; expand for the plan + activity.
export function MissionResultCard({ projectId, missionId }: { projectId: string; missionId: string }) {
  const [detail, setDetail] = useState<MissionDetailView | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let live = true;
    getMission(projectId, missionId)
      .then((d) => live && setDetail(d))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [projectId, missionId]);
  if (!detail) return null;
  const m = detail.mission;
  const ok = m.status === "done";
  const done = detail.tasks.filter((t) => t.status === "done").length;
  return (
    <div className={cn("mb-2 rounded-xl border p-2.5 text-sm", ok ? "border-success/30 bg-success/[0.04]" : "border-destructive/30 bg-destructive/[0.04]")}>
      <button className="flex w-full items-center gap-2 text-left" onClick={() => setOpen((o) => !o)}>
        {ok ? <CheckCircle2 className="h-4 w-4 shrink-0 text-success" /> : <XCircle className="h-4 w-4 shrink-0 text-destructive" />}
        <span className="min-w-0 flex-1 truncate font-medium">{missionTitle(m)}</span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {ok ? "completed" : "stopped"}
          {detail.tasks.length > 0 ? ` · ${done}/${detail.tasks.length} steps` : ""} · breakdown
        </span>
        <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="mt-2 space-y-2 border-t pt-2">
          <PlanChecklist tasks={detail.tasks} live={false} surface="result" />
          {activityRows(detail.events).length > 0 && <Timeline events={detail.events} />}
        </div>
      )}
    </div>
  );
}
